export interface SparklineGeometry {
  /** [x, y] pairs, left to right, y grows downward. */
  points: Array<[number, number]>
  /** y of the dashed baseline (the first value, i.e. starting equity). */
  baselineY: number
}

const PAD_Y = 2

/**
 * Maps equity values into a `width` x `height` box. The y range always
 * includes the baseline (the first value). `fraction` in (0, 1] draws only the
 * first `ceil(fraction * n)` points, but x spacing is that of the full series,
 * so a half-elapsed run fills half the width.
 */
export function sparklinePoints(
  values: number[] | null,
  width: number,
  height: number,
  fraction: number,
): SparklineGeometry {
  if (!values || values.length === 0) return { points: [], baselineY: height / 2 }
  const n = values.length
  const baseline = values[0]
  let min = baseline
  let max = baseline
  for (const v of values) {
    if (v < min) min = v
    if (v > max) max = v
  }
  const span = max - min
  const inner = height - 2 * PAD_Y
  const yOf = (v: number): number => (span === 0 ? height / 2 : PAD_Y + (1 - (v - min) / span) * inner)
  const clamped = Math.min(1, Math.max(0, fraction))
  const count = Math.min(n, Math.max(1, Math.ceil(clamped * n)))
  const xStep = n > 1 ? width / (n - 1) : 0
  const points: Array<[number, number]> = []
  for (let i = 0; i < count; i += 1) points.push([i * xStep, yOf(values[i])])
  return { points, baselineY: yOf(baseline) }
}

/** `x,y x,y ...` for an SVG polyline. */
export function pointsAttr(points: Array<[number, number]>): string {
  return points.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(' ')
}
